// Mock data for Ruben P. Wargner Executive Biography

export const heroData = {
  name: "Ruben P. Wargner",
  title: "Founder & Chief Strategy Officer",
  company: "Redspire Civil Engineering",
  tagline: "Shaping Infrastructure Excellence Across Continents",
  avatarUrl: "https://customer-assets.emergentagent.com/job_a38efa49-e796-4faa-a77b-25f4f0ea2e3e/artifacts/vx6ol7d8_avater.png",
  heroBackground: "https://images.unsplash.com/photo-1604578952737-1fe4c349bd73?w=1920&q=80"
};

export const achievementsData = [
  {
    id: 1,
    title: "NorthWind River Bridge Expansion",
    year: "2019",
    description: "Guided strategic structural modernization increasing load capacity by 34%.",
    image: "https://images.unsplash.com/photo-1529926691761-20fb82067c71?w=800&q=80"
  },
  {
    id: 2,
    title: "Metropolitan Transit Corridor",
    year: "2021",
    description: "Led design oversight for 45-mile urban rail expansion serving 2.3M daily commuters.",
    image: "https://images.unsplash.com/photo-1536099629323-44806c1ea264?w=800&q=80"
  },
  {
    id: 3,
    title: "Alpine Tunnel System",
    year: "2017",
    description: "Pioneered innovative boring techniques reducing construction timeline by 18 months.",
    image: "https://images.pexels.com/photos/1057858/pexels-photo-1057858.jpeg?w=800"
  },
  {
    id: 4,
    title: "Coastal Highway Interchange",
    year: "2020",
    description: "Engineered seismic-resistant infrastructure for critical evacuation route.",
    image: "https://images.unsplash.com/photo-1519043916581-33ecfdba3b1c?w=800&q=80"
  },
  {
    id: 5,
    title: "Harbor District Redevelopment",
    year: "2022",
    description: "Transformed 120-acre industrial zone into sustainable mixed-use development.",
    image: "https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?w=800&q=80"
  },
  {
    id: 6,
    title: "Continental Rail Network",
    year: "2023",
    description: "Oversaw cross-border infrastructure connecting 4 nations with high-speed rail.",
    image: "https://images.unsplash.com/photo-1527335988388-b40ee248d80c?w=800&q=80"
  }
];

export const executiveSummary = {
  intro: "Ruben P. Wargner stands at the intersection of visionary leadership and engineering excellence. With over three decades of transformative experience in civil infrastructure, he has established himself as one of the most influential figures shaping the built environment across North America and Central Europe.",
  body: "As Founder and Chief Strategy Officer of Redspire Civil Engineering, Wargner brings a unique cross-continental perspective, drawing from his Czech-American heritage to bridge diverse engineering philosophies and methodologies. His strategic vision has guided the successful delivery of infrastructure projects exceeding $4 billion in cumulative value, fundamentally improving the lives of millions who depend on these critical systems daily."
};

export const biographyData = {
  sections: [
    {
      title: "Early Life & Education",
      content: "Born in Prague during the final years of Czechoslovakia, Ruben Wargner was raised in a family deeply rooted in engineering tradition. His grandfather supervised the construction of several iconic bridges across the Vltava River, instilling in young Ruben a profound respect for structures that connect communities.",
      quote: "Every bridge is a promise—a commitment to connect what was once divided."
    },
    {
      title: "Transatlantic Journey",
      content: "Following the Velvet Revolution, Wargner emigrated to the United States in 1991, carrying with him not just technical expertise but a European sensibility for infrastructure that serves generations. He completed his Master's in Structural Engineering at MIT, where his thesis on seismic-resistant bridge design earned the prestigious George Macomber Award."
    },
    {
      title: "Building Redspire",
      content: "In 2003, Wargner founded Redspire Civil Engineering with a singular vision: to deliver infrastructure solutions that balance bold innovation with timeless durability. What began as a three-person consultancy has grown into a multinational firm employing over 800 engineers and project managers across 12 offices worldwide.",
      quote: "We don't just build structures—we engineer legacies."
    },
    {
      title: "Philosophy & Approach",
      content: "Wargner's leadership philosophy emphasizes what he terms 'generational thinking'—designing and building with a 100-year horizon. This approach has attracted partnerships with sovereign wealth funds, development banks, and government agencies seeking infrastructure that transcends political cycles and short-term economic pressures."
    }
  ]
};

export const timelineData = [
  {
    year: "1989",
    title: "Civil Engineering Degree",
    description: "Graduated with honors from Czech Technical University in Prague",
    icon: "GraduationCap"
  },
  {
    year: "1991",
    title: "Immigration to United States",
    description: "Relocated to Boston to pursue advanced studies at MIT",
    icon: "Plane"
  },
  {
    year: "1993",
    title: "MIT Master's Degree",
    description: "Completed M.S. in Structural Engineering with thesis honors",
    icon: "Award"
  },
  {
    year: "1994-2002",
    title: "Senior Engineer, Bechtel Corporation",
    description: "Led structural design teams on major infrastructure projects",
    icon: "Building2"
  },
  {
    year: "2003",
    title: "Founded Redspire Civil Engineering",
    description: "Launched firm with vision for generational infrastructure",
    icon: "Rocket"
  },
  {
    year: "2010",
    title: "European Expansion",
    description: "Opened Prague and Vienna offices, returning to Central Europe",
    icon: "Globe"
  },
  {
    year: "2015",
    title: "IBC Leadership Ribbon",
    description: "Awarded for excellence in bridge engineering leadership",
    icon: "Medal"
  },
  {
    year: "2020",
    title: "$4B Project Milestone",
    description: "Surpassed $4 billion in cumulative project delivery",
    icon: "TrendingUp"
  },
  {
    year: "2024",
    title: "Appointed to Federal Advisory Board",
    description: "Named to U.S. Infrastructure Modernization Council",
    icon: "Crown"
  }
];

export const leadershipStats = [
  {
    value: "30+",
    label: "Years Civil Engineering Experience",
    icon: "Calendar"
  },
  {
    value: "7",
    label: "Countries with Active Projects",
    icon: "Globe"
  },
  {
    value: "$4B+",
    label: "Infrastructure Projects Overseen",
    icon: "Building2"
  },
  {
    value: "800+",
    label: "Engineers & Specialists Employed",
    icon: "Users"
  }
];

export const expertiseData = [
  { name: "Structural Engineering", size: "large" },
  { name: "Bridge Design", size: "large" },
  { name: "Infrastructure Strategy", size: "large" },
  { name: "Seismic Analysis", size: "medium" },
  { name: "Tunnel Engineering", size: "medium" },
  { name: "Transportation Planning", size: "medium" },
  { name: "Public-Private Partnerships", size: "medium" },
  { name: "Project Management", size: "medium" },
  { name: "Risk Assessment", size: "small" },
  { name: "Geotechnical Analysis", size: "small" },
  { name: "Environmental Compliance", size: "small" },
  { name: "Cost Engineering", size: "small" },
  { name: "BIM Technology", size: "small" },
  { name: "Sustainable Design", size: "small" },
  { name: "Value Engineering", size: "small" }
];

export const publicationsData = [
  {
    title: "The Future of Cross-Continental Infrastructure Networks",
    source: "Engineering News-Record",
    year: "2023",
    description: "An in-depth analysis of emerging trends in international infrastructure development and the role of public-private partnerships.",
    logo: "ENR"
  },
  {
    title: "Generational Thinking in Civil Engineering",
    source: "ASCE Civil Engineering Magazine",
    year: "2022",
    description: "Wargner's influential piece on designing infrastructure with 100-year horizons.",
    logo: "ASCE"
  },
  {
    title: "Bridging Cultures: European Innovation in American Infrastructure",
    source: "Construction Dive",
    year: "2021",
    description: "Interview feature exploring how Czech engineering traditions influence modern American projects.",
    logo: "CD"
  }
];

export const contactData = {
  email: "ruben@redspire.com",
  linkedin: "https://www.linkedin.com/in/ruben-wargner-59a83b336",
  company: "https://redspirecivil.com"
};
